import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Heart, Send, TrendingUp, DollarSign } from "lucide-react";
import ConnectCard from "../components/ConnectCard";

export default function Payouts() {
    const { user, loading, refresh } = useAuth();
    const navigate = useNavigate();
    const [tips, setTips] = useState([]);
    const [top, setTop] = useState([]);
    const [method, setMethod] = useState("paypal");
    const [handle, setHandle] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/login");
    }, [loading, user, navigate]);

    const load = async () => {
        try {
            const [t, tp] = await Promise.all([
                api.get("/tips/received"),
                api.get("/tips/top-tippers"),
            ]);
            setTips(t.data);
            setTop(tp.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { if (user) load(); }, [user]);

    const requestPayout = async () => {
        if (!handle.trim()) { toast.error("Enter a payout handle"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/tips/request-payout", { method, handle: handle.trim() });
            toast.success(`Payout of $${data.amount_usd.toFixed(2)} requested`);
            setHandle("");
            refresh();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSubmitting(false); }
    };

    if (!user) return <main className="max-w-3xl mx-auto p-8 font-bold text-[#4A4A4A]">Loading…</main>;

    const balance = user.tip_balance_usd || 0;
    const canPayout = balance >= 10;

    return (
        <main className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-lg brut-static bg-[#FFB8D1] flex items-center justify-center">
                    <Heart className="w-5 h-5 fill-[#FF5A5F] text-[#FF5A5F]" />
                </div>
                <h1 className="display text-3xl sm:text-4xl font-black">Your tips</h1>
            </div>

            <section className="grid sm:grid-cols-3 gap-3 mb-8">
                <div className="brut-static bg-[#FFE55C] rounded-xl p-5" data-testid="payout-balance">
                    <div className="text-xs font-black uppercase tracking-wider text-[#0A0A0A]/70">Balance</div>
                    <div className="display text-4xl font-black mt-1">${balance.toFixed(2)}</div>
                    <div className="text-xs font-medium text-[#0A0A0A]/70 mt-1">Min payout $10</div>
                </div>
                <div className="brut-static bg-white rounded-xl p-5" data-testid="payout-tips-count">
                    <div className="text-xs font-black uppercase tracking-wider text-[#0A0A0A]/70">Tips received</div>
                    <div className="display text-4xl font-black mt-1">{user.tips_received_count || 0}</div>
                </div>
                <div className="brut-static bg-white rounded-xl p-5" data-testid="payout-avg">
                    <div className="text-xs font-black uppercase tracking-wider text-[#0A0A0A]/70">Avg tip</div>
                    <div className="display text-4xl font-black mt-1">
                        ${tips.length ? (tips.reduce((a, t) => a + (t.gross_usd || 0), 0) / tips.length).toFixed(2) : "0.00"}
                    </div>
                </div>
            </section>

            <ConnectCard />

            <section className="brut-static bg-white rounded-xl p-5 mb-8">
                <div className="flex items-center gap-2 mb-3">
                    <Send className="w-5 h-5" />
                    <h2 className="display text-xl font-black">Request payout</h2>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                    <select
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                        className="px-3 py-3 rounded-lg brut-inset bg-white font-bold text-sm focus:outline-none"
                        data-testid="payout-method"
                    >
                        <option value="paypal">PayPal</option>
                        <option value="bank">Bank / Wire</option>
                        <option value="stripe_connect">Stripe Connect</option>
                    </select>
                    <input
                        value={handle}
                        onChange={(e) => setHandle(e.target.value)}
                        placeholder={method === "paypal" ? "you@example.com" : method === "bank" ? "Account details" : "acct_… id"}
                        className="sm:col-span-2 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none"
                        data-testid="payout-handle"
                    />
                </div>
                <button
                    onClick={requestPayout}
                    disabled={!canPayout || submitting}
                    className="mt-4 w-full sm:w-auto px-5 py-3 rounded-lg brut bg-[#0A0A0A] text-white font-bold disabled:opacity-50"
                    data-testid="payout-submit"
                >
                    {submitting ? "Requesting…" : canPayout ? `Request $${balance.toFixed(2)}` : "Need $10 minimum"}
                </button>
                <p className="mt-3 text-[11px] text-[#4A4A4A] font-medium">
                    Payouts are processed manually within 3-5 business days. Once requested, your balance resets to $0.
                </p>
            </section>

            <section className="grid md:grid-cols-2 gap-5">
                <div className="brut-static bg-white rounded-xl p-5" data-testid="top-tippers">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5" />
                        <h2 className="display text-xl font-black">Top tippers</h2>
                    </div>
                    {top.length === 0 ? (
                        <div className="text-sm text-[#4A4A4A] font-medium italic">No tips yet.</div>
                    ) : (
                        <ul className="space-y-2">
                            {top.map((t) => (
                                <li key={t.username} className="flex items-center justify-between text-sm font-medium">
                                    <span className="font-bold">@{t.username}</span>
                                    <span className="font-black">${t.total_usd.toFixed(2)} <span className="text-xs text-[#4A4A4A]">· {t.count} tip{t.count === 1 ? "" : "s"}</span></span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="brut-static bg-white rounded-xl p-5" data-testid="recent-tips">
                    <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="w-5 h-5" />
                        <h2 className="display text-xl font-black">Recent tips</h2>
                    </div>
                    {tips.length === 0 ? (
                        <div className="text-sm text-[#4A4A4A] font-medium italic">No tips yet — post more, get more!</div>
                    ) : (
                        <ul className="space-y-3 max-h-72 overflow-y-auto">
                            {tips.slice(0, 20).map((t) => (
                                <li key={`${t.created_at}-${t.from_username}`} className="text-sm border-b border-[#0A0A0A]/10 pb-2 last:border-none">
                                    <div className="flex items-center justify-between">
                                        <span className="font-bold">@{t.from_username}</span>
                                        <span className="font-black">+${t.net_usd?.toFixed(2)}</span>
                                    </div>
                                    {t.message && <div className="text-xs text-[#4A4A4A] italic mt-0.5">"{t.message}"</div>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>
        </main>
    );
}
